import styles from './style.module.scss';
import {
  StepperOptions,
  TransactionModalResponseContentStepperStepProps,
} from './props';
import {
  getStepDescription,
  getSteperIcon,
  getStepperStatus,
  getDepositStepTitle,
  getWithdrawStepTitle,
} from './helper';
import { useTranslation } from '../../../../../../hooks';
import { CommonModalsKey } from '../../../../../../enums';
import { useCurrentModal } from '../../../../../../hooks/modals/useCurrentModal';
import { Deposit } from '../../../../../../models';
import {
  DepositStatusId,
  DepositStatusIdType,
} from '@starsoft/common/models/deposit/status/id';
import { WithdrawalStatusIdType } from '@starsoft/common/models/withdraw/status/id';

/**
 * TransactionModalResponseContentStepperStep Component
 * Renders a single step in the transaction stepper UI, showing status, title, description and optional payment link.
 * The step appearance changes based on transaction type (deposit/withdrawal) and current status.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.position - The position/index of this step in the stepper (0-2)
 * @param {TransactionType} props.transaction - The transaction object containing status, dates and payment details
 * @returns {JSX.Element} A step container with indicator, title, description and optional payment link
 *
 * @example
 * const transaction = {
 *   status: {
 *     id: "1", // DepositStatusId.Created
 *     name: { en: "Created" }
 *   },
 *   createdAt: "2023-12-25T10:30:00Z",
 *   paymentLink: {
 *     url: "https://payment.provider.com/tx123"
 *   }
 * };
 *
 * <TransactionModalResponseContentStepperStep
 *   position={1}
 *   transaction={transaction}
 * />
 */
export default function TransactionModalResponseContentStepperStep({
  position,
  transaction,
}: TransactionModalResponseContentStepperStepProps) {
  const { t, lang } = useTranslation('common');
  const modal: string | undefined = useCurrentModal(CommonModalsKey);
  const formUrl: string = (transaction as Deposit)?.paymentLink?.url;
  const title: string =
    modal === CommonModalsKey.Deposit
      ? getDepositStepTitle(position)
      : getWithdrawStepTitle(position);
  const description: string = getStepDescription(position);
  const icon: string = getSteperIcon(
    position,
    transaction?.status?.id as DepositStatusIdType | WithdrawalStatusIdType,
  );
  const itemStatus: string = getStepperStatus(
    position,
    transaction?.status?.id as DepositStatusIdType | WithdrawalStatusIdType,
  );

  return (
    <div
      className={`${styles.container} ${styles[`container--${itemStatus}`]}`}
    >
      <div
        className={`${styles.container__indicator} ${styles[`container__indicator--${itemStatus}`]}`}
      >
        <i className={icon} />
      </div>
      <div className={styles.container__column}>
        <div className={styles.container__column__label}>
          {t(title, {
            status: transaction?.status?.name?.[lang] ?? '',
          })}
        </div>
        {position != StepperOptions.Result && (
          <div className={styles.container__column__description}>
            {t(description, {
              createdAt: Intl.DateTimeFormat(lang, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(transaction?.createdAt)),
            })}
          </div>
        )}
        {formUrl &&
          position == StepperOptions.Processing &&
          transaction?.status?.id?.toString() ==
            DepositStatusId.Created?.toString() && (
            <a
              href={formUrl}
              target="_blank"
              className={styles.container__column__link}
              rel="noreferrer"
            >
              <div>{formUrl}</div>
              <i className="fa-solid fa-arrow-up-right-from-square" />
            </a>
          )}
      </div>
    </div>
  );
}
